"use client"

import { useState } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { Loader2 } from "lucide-react"

import {
  AUTH_CARD,
  AUTH_LINK,
  AUTH_SUBMIT,
  AUTH_TITLE,
} from "@/components/auth/auth-styles"
import { PasswordInput } from "@/components/password-input"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { authClient } from "@/lib/auth-client"
import { authErrorMessage } from "@/lib/auth-errors"

export function SignupForm() {
  const router = useRouter()
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (loading) return

    const form = new FormData(event.currentTarget)
    const name = String(form.get("name")).trim()
    const email = String(form.get("email")).trim()
    const password = String(form.get("password"))

    if (!name || !email || !password) {
      setError("Bitte fülle alle Felder aus.")
      return
    }
    if (password !== String(form.get("confirm-password"))) {
      setError("Die Passwörter stimmen nicht überein.")
      return
    }

    setError("")
    setLoading(true)
    const result = await authClient.signUp.email({ email, password, name })
    if (result.error) {
      setError(
        authErrorMessage(
          result.error,
          "Registrierung fehlgeschlagen. Versuch es noch einmal."
        )
      )
      setLoading(false)
      return
    }

    // Better Auth signs the user in right after sign-up
    router.replace("/")
    router.refresh()
  }

  return (
    <Card className={AUTH_CARD}>
      <CardHeader>
        <CardTitle className={AUTH_TITLE}>Konto erstellen</CardTitle>
        <CardDescription>
          Nur freigeschaltete E-Mail-Adressen können sich registrieren.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} noValidate>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="name">Name</FieldLabel>
              <Input id="name" name="name" autoComplete="name" />
            </Field>
            <Field>
              <FieldLabel htmlFor="email">E-Mail</FieldLabel>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="name@beispiel.de"
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="password">Passwort</FieldLabel>
              <PasswordInput
                id="password"
                name="password"
                autoComplete="new-password"
              />
              <FieldDescription>Mindestens 8 Zeichen.</FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="confirm-password">
                Passwort wiederholen
              </FieldLabel>
              <PasswordInput
                id="confirm-password"
                name="confirm-password"
                autoComplete="new-password"
              />
            </Field>

            {error && (
              <p
                role="alert"
                className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {error}
              </p>
            )}

            <Field>
              <Button
                type="submit"
                size="lg"
                className={AUTH_SUBMIT}
                aria-busy={loading}
              >
                {loading && <Loader2 className="animate-spin" />}
                Konto erstellen
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Schon ein Konto?{" "}
                <Link href="/login" className={AUTH_LINK}>
                  Anmelden
                </Link>
              </p>
            </Field>
          </FieldGroup>
        </form>
      </CardContent>
    </Card>
  )
}
